import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, Sun, Snowflake, Leaf, Flower2, Calendar, ChevronRight, Tag, Flame, Pause, Play, Star, Crown, Droplets, Zap, Layers } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import InlineBookingFlow from "./InlineBookingFlow";
import { usePopularTreatmentsBannerEnabled } from "@/hooks/usePopularTreatmentsBannerEnabled";

const SeasonalSuggestions = ({ onBookNow, scrollToSection, className = "" }) => {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const bannerEnabled = usePopularTreatmentsBannerEnabled();
  const [isPaused, setIsPaused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showBooking, setShowBooking] = useState(false);
  const [bookingPackage, setBookingPackage] = useState(null);
  const navigate = useNavigate();

  // All 30 Skin Boosters & Combos treatments
  const allPromos = [
    // CORE SKIN BOOSTER TREATMENTS (1-10)
    { num: 1, name: "The Baby Glow Infusion", subtitle: "Face", price: "$699", priceNum: 699, reason: "Full face hydration + texture refinement", tag: "SKIN BOOSTER", tagColor: "bg-teal-500", hasSkinBooster: true, hasBotox: false },
    { num: 2, name: "The Radiance Lift", subtitle: "Face + Neck", price: "$899", priceNum: 899, reason: "Face + neck smoothing + elasticity", tag: "SKIN BOOSTER", tagColor: "bg-teal-500", hasSkinBooster: true, hasBotox: false },
    { num: 3, name: "The Platinum Skin Renewal", subtitle: "Face + Neck + Décolleté", price: "$1,099", priceNum: 1099, reason: "Full upper body rejuvenation", tag: "PREMIUM", tagColor: "bg-amber-500", hasSkinBooster: true, hasBotox: false },
    { num: 4, name: "The Eye Revival", subtitle: "Under Eyes", price: "$449", priceNum: 449, reason: "Fine lines + crepey skin correction", tag: "SKIN BOOSTER", tagColor: "bg-teal-500", hasSkinBooster: true, hasBotox: false },
    { num: 5, name: "The Neck Refinement", subtitle: "Neck", price: "$499", priceNum: 499, reason: "Horizontal lines + tightening", tag: "SKIN BOOSTER", tagColor: "bg-teal-500", hasSkinBooster: true, hasBotox: false },
    { num: 6, name: "The Décolleté Glow", subtitle: "Chest", price: "$599", priceNum: 599, reason: "Chest rejuvenation + sun damage repair", tag: "SKIN BOOSTER", tagColor: "bg-teal-500", hasSkinBooster: true, hasBotox: false },
    { num: 7, name: "The Hand Rejuvenation", subtitle: "Hands", price: "$499", priceNum: 499, reason: "Restore hydration + youthful skin", tag: "SKIN BOOSTER", tagColor: "bg-teal-500", hasSkinBooster: true, hasBotox: false },
    // Iter 358 · Feb 13 2026 · Karine ask — removed "The Skin Precision" ($399).
    { num: 10, name: "The Total Skin Boost", subtitle: "3 Areas", price: "$999", priceNum: 999, reason: "Any 3 areas combined", tag: "BUNDLE", tagColor: "bg-violet-500", hasSkinBooster: true, hasBotox: false },
    
    // SKIN PROGRAMS (11-15)
    { num: 11, name: "The Glass Skin Program", subtitle: "Face x3", price: "$1,799", priceNum: 1799, reason: "3 treatments (best results protocol)", tag: "BEST VALUE", tagColor: "bg-emerald-500", hasSkinBooster: true, hasBotox: false },
    { num: 12, name: "The Radiance Program", subtitle: "Face + Neck x3", price: "$2,599", priceNum: 2599, reason: "3 sessions for optimal results", tag: "BEST VALUE", tagColor: "bg-emerald-500", hasSkinBooster: true, hasBotox: false },
    { num: 13, name: "The Platinum Program", subtitle: "Face + Neck + Décolleté x3", price: "$2,999", priceNum: 2999, reason: "Complete upper body transformation", tag: "PREMIUM", tagColor: "bg-amber-500", hasSkinBooster: true, hasBotox: false },
    { num: 14, name: "The Eye Perfection Program", subtitle: "Under Eyes x3", price: "$1,199", priceNum: 1199, reason: "3 sessions for delicate eye area", tag: "PROGRAM", tagColor: "bg-blue-500", hasSkinBooster: true, hasBotox: false },
    { num: 15, name: "The Signature Skin Membership", subtitle: "Quarterly Plan", price: "$249/mo", priceNum: 249, reason: "Skin booster maintenance + perks", tag: "MEMBERSHIP", tagColor: "bg-rose-500", hasSkinBooster: true, hasBotox: false },
    
    // BOTOX ONLY (16-18)
    { num: 16, name: "The Smooth Eyes", subtitle: "Crow's Feet", price: "$299", priceNum: 299, reason: "Targeted crow's feet treatment", tag: "BOTOX", tagColor: "bg-purple-500", hasSkinBooster: false, hasBotox: true },
    { num: 17, name: "The Upper Face Smooth", subtitle: "7 Areas", price: "$599", priceNum: 599, reason: "Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, Lip Flip", tag: "BOTOX", tagColor: "bg-purple-500", hasSkinBooster: false, hasBotox: true },
    { num: 18, name: "The Full Upper Face Relax", subtitle: "8 Areas + Neck", price: "$799", priceNum: 799, reason: "All 7 areas PLUS Neck Bands", tag: "BOTOX", tagColor: "bg-purple-500", hasSkinBooster: false, hasBotox: true },
    
    // SKIN BOOSTER + BOTOX COMBOS (19-27)
    { num: 19, name: "The Starter Glow", subtitle: "3 Botox Areas + Baby Glow", price: "$899", priceNum: 899, reason: "11's + Brow Lift + Smile/Lip/Bunny + Baby Glow", tag: "COMBO", tagColor: "bg-gradient-to-r from-purple-500 to-teal-500", hasSkinBooster: true, hasBotox: true },
    { num: 31, name: "The Nefertiti Glow", subtitle: "Jawline + Neck Lift", price: "$899", priceNum: 899, reason: "Nefertiti Lift Botox + Baby Glow hydration", tag: "MOST POPULAR", tagColor: "bg-rose-500", hasSkinBooster: true, hasBotox: true },
    { num: 20, name: "The Mini Refresh Glow", subtitle: "4 Botox Areas + Baby Glow", price: "$999", priceNum: 999, reason: "Crow's Feet + Forehead + Brow + Smile + Baby Glow", tag: "COMBO", tagColor: "bg-gradient-to-r from-purple-500 to-teal-500", hasSkinBooster: true, hasBotox: true },
    { num: 21, name: "The Big 3 Glow", subtitle: "Big 3 Areas + Baby Glow", price: "$1,099", priceNum: 1099, reason: "Crow's Feet + 11's + Forehead + Baby Glow", tag: "POPULAR", tagColor: "bg-rose-500", hasSkinBooster: true, hasBotox: true },
    { num: 22, name: "The #1 Choice Glow", subtitle: "7 Botox Areas + Baby Glow", price: "$1,199", priceNum: 1199, reason: "ALL 7 AREAS + Baby Glow Face", tag: "#1 CHOICE", tagColor: "bg-emerald-500", hasSkinBooster: true, hasBotox: true },
    { num: 23, name: "The Total Face Glow", subtitle: "8 Areas + Neck + Baby Glow", price: "$1,399", priceNum: 1399, reason: "ALL 8 AREAS incl. Neck Bands + Baby Glow", tag: "COMBO", tagColor: "bg-gradient-to-r from-purple-500 to-teal-500", hasSkinBooster: true, hasBotox: true },
    { num: 24, name: "The Complete Rejuvenation", subtitle: "7 Areas + Face & Neck Glow", price: "$1,599", priceNum: 1599, reason: "7 Botox Areas + Face & Neck Baby Glow", tag: "BEST SELLER", tagColor: "bg-rose-500", hasSkinBooster: true, hasBotox: true },
    { num: 25, name: "The Platinum Rejuvenation", subtitle: "8 Areas + Full Body Glow", price: "$1,899", priceNum: 1899, reason: "8 Areas + Face, Neck & Décolleté Glow", tag: "PREMIUM", tagColor: "bg-amber-500", hasSkinBooster: true, hasBotox: true },
    { num: 26, name: "The Eye Brightening Combo", subtitle: "Crow's Feet + Under-Eye", price: "$699", priceNum: 699, reason: "Crow's Feet Botox + Under-Eye Baby Glow", tag: "COMBO", tagColor: "bg-gradient-to-r from-purple-500 to-teal-500", hasSkinBooster: true, hasBotox: true },
    { num: 27, name: "The Neck Perfection Combo", subtitle: "Neck Bands + Neck Glow", price: "$799", priceNum: 799, reason: "Neck Bands Botox + Neck Baby Glow", tag: "COMBO", tagColor: "bg-gradient-to-r from-purple-500 to-teal-500", hasSkinBooster: true, hasBotox: true },
    
    // VIP / HIGH-TICKET (28-30)
    { num: 28, name: "The VIP Glass Skin Experience", subtitle: "High End", price: "$1,999", priceNum: 1999, reason: "Full Botox + Platinum Skin Renewal", tag: "VIP", tagColor: "bg-amber-600", hasSkinBooster: true, hasBotox: true, category: "vip" },
    { num: 29, name: "The Ultimate Skin Transformation", subtitle: "3 Month Program", price: "$3,499", priceNum: 3499, reason: "3 Full Face Botox + 3 Skin Booster sessions", tag: "VIP", tagColor: "bg-amber-600", hasSkinBooster: true, hasBotox: true, category: "vip" },
    { num: 30, name: "The Crown Reserve Signature", subtitle: "Ultimate VIP", price: "$4,999", priceNum: 4999, reason: "Full year of premium treatments + VIP perks", tag: "ULTIMATE", tagColor: "bg-amber-600", hasSkinBooster: true, hasBotox: true, category: "vip" },
  ];

  // Category filter options
  const categories = [
    { id: 'all', label: isFr ? 'Tous' : 'All', icon: Sparkles },
    { id: 'boosters', label: isFr ? 'Boosters' : 'Boosters', icon: Droplets },
    { id: 'botox', label: 'Botox', icon: Zap },
    { id: 'combos', label: 'Combos', icon: Layers },
    { id: 'vip', label: 'VIP', icon: Crown },
  ];

  // Filter treatments by category
  const getFilteredPromos = () => {
    if (selectedCategory === 'all') return allPromos;
    if (selectedCategory === 'boosters') return allPromos.filter(p => p.hasSkinBooster && !p.hasBotox);
    if (selectedCategory === 'botox') return allPromos.filter(p => p.hasBotox && !p.hasSkinBooster);
    if (selectedCategory === 'combos') return allPromos.filter(p => p.hasSkinBooster && p.hasBotox && p.category !== 'vip');
    if (selectedCategory === 'vip') return allPromos.filter(p => p.category === 'vip');
    return allPromos;
  };

  const filteredPromos = getFilteredPromos();

  // Handle direct booking from carousel
  const handleBookPromo = (promo) => {
    // Determine the service type for Square mapping
    let serviceType = 'booster';
    let duration = 30;
    
    if (promo.hasBotox && !promo.hasSkinBooster) {
      serviceType = 'botox';
      duration = 30;
    } else if (promo.hasSkinBooster && promo.hasBotox) {
      if (promo.category === 'vip') {
        serviceType = 'vip';
        duration = 60;
      } else {
        serviceType = 'combo';
        duration = 45;
      }
    }
    
    // Build badges array
    const badges = [];
    if (promo.hasSkinBooster) badges.push('REDENSITY 1');
    if (promo.hasBotox) badges.push('BOTOX');
    
    setBookingPackage({
      num: promo.num,
      name: promo.name,
      subtitle: promo.subtitle,
      price: promo.price,
      description: promo.reason,
      type: serviceType,
      duration: duration,
      badges: badges
    });
    
    setShowBooking(true);
  };

  // Calculate Afterpay payment (4 installments)
  const getAfterpay = (price) => {
    const installment = (price / 4).toFixed(2);
    return `4 x $${installment}`;
  };

  // Duplicate array for seamless infinite scroll
  const duplicatedPromos = [...filteredPromos, ...filteredPromos];

  // Dynamic animation duration based on number of items
  const animationDuration = `${filteredPromos.length * 3}s`;

  // Fixed gradient for Skin Boosters & Combos section
  const sectionGradient = "from-amber-500 to-amber-600";

  // Respect the "Popular Treatments Scrolling Banner" automation toggle.
  // When OFF, render nothing so the banner is hidden sitewide without a
  // redeploy. Placed AFTER all hooks to comply with rules-of-hooks.
  if (!bannerEnabled) return null;

  return (
    <section className={`py-6 md:py-10 px-3 md:px-4 ${className}`} data-testid="seasonal-banner">
      <div className="max-w-6xl mx-auto">
        <div className="bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-200 rounded-xl md:rounded-2xl overflow-hidden shadow-sm">
          {/* Header with Skin Boosters & Combos branding */}
          <div className={`bg-gradient-to-r ${sectionGradient} px-4 md:px-6 py-3 md:py-4 flex items-center justify-between`}>
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Droplets className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <div className="text-white">
                <h3 className="font-semibold text-sm md:text-lg flex items-center gap-1 md:gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">{isFr ? "Skin Boosters & Combos" : "Skin Boosters & Combos"}</span>
                  <span className="sm:hidden">{isFr ? "Skin Boosters" : "Skin Boosters"}</span>
                  {/* BIG NEW! BADGE */}
                  <span className="ml-1 md:ml-2 px-2 md:px-3 py-0.5 md:py-1 bg-white text-amber-600 text-[10px] md:text-sm font-black rounded-full animate-pulse shadow-lg">
                    NEW!
                  </span>
                </h3>
                <p className="text-[10px] md:text-xs opacity-90">
                  {selectedCategory === 'all' 
                    ? `${allPromos.length} ${isFr ? "traitements" : "treatments"}`
                    : `${filteredPromos.length} ${isFr ? "de" : "of"} ${allPromos.length}`
                  }
                </p>
              </div>
            </div>
            
            {/* Pause/Play button */}
            <button 
              onClick={() => setIsPaused(!isPaused)}
              className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white text-[10px] md:text-xs"
            >
              {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              <span className="hidden sm:inline">{isPaused ? (isFr ? "Reprendre" : "Resume") : (isFr ? "Pause" : "Pause")}</span>
            </button>
          </div>

          {/* Category Filter Tabs */}
          <div className="px-3 md:px-4 py-2 md:py-3 bg-stone-50 border-b border-stone-200 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1.5 md:gap-2 min-w-max">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.id;
                const count = cat.id === 'all' ? allPromos.length 
                  : cat.id === 'boosters' ? allPromos.filter(p => p.hasSkinBooster && !p.hasBotox).length
                  : cat.id === 'botox' ? allPromos.filter(p => p.hasBotox && !p.hasSkinBooster).length
                  : cat.id === 'combos' ? allPromos.filter(p => p.hasSkinBooster && p.hasBotox && p.category !== 'vip').length
                  : allPromos.filter(p => p.category === 'vip').length;
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all ${
                      isActive 
                        ? 'bg-amber-500 text-white shadow-md' 
                        : 'bg-white text-gray-600 hover:bg-amber-50 hover:text-amber-700 border border-stone-200'
                    }`}
                    data-testid={`filter-${cat.id}`}
                  >
                    <Icon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    <span>{cat.label}</span>
                    <span className={`px-1 md:px-1.5 py-0.5 rounded-full text-[8px] md:text-[10px] ${
                      isActive ? 'bg-white/20' : 'bg-stone-100'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Smooth Scrolling Carousel */}
          <div 
            className="py-4 md:py-6 overflow-hidden"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setTimeout(() => setIsPaused(false), 3000)}
          >
            <div 
              className="flex gap-3 md:gap-4 px-3 md:px-4"
              style={{
                animation: isPaused ? 'none' : `smoothScroll ${animationDuration} linear infinite`,
                width: 'fit-content',
              }}
            >
              {duplicatedPromos.map((promo, index) => (
                <div
                  key={`${promo.num}-${index}`}
                  className="relative bg-white rounded-lg md:rounded-xl p-3 md:p-4 border border-stone-100 hover:shadow-lg hover:border-amber-200 transition-all cursor-pointer group flex-shrink-0"
                  style={{ width: '220px' }}
                  onClick={() => handleBookPromo(promo)}
                  data-testid={`promo-card-${promo.num}`}
                >
                  {/* Number Badge - Large and prominent */}
                  <div className="absolute -top-2 -left-1 md:-top-3 md:-left-2 w-6 h-6 md:w-8 md:h-8 rounded-full bg-stone-800 text-white flex items-center justify-center font-bold text-xs md:text-sm shadow-lg">
                    #{promo.num}
                  </div>
                  
                  {/* Tag Badge */}
                  <div className={`absolute -top-1.5 right-1 md:-top-2 md:right-2 ${promo.tagColor} text-white text-[8px] md:text-[10px] font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded-full shadow-sm`}>
                    {promo.tag}
                  </div>
                  
                  {/* Content */}
                  <div className="mt-1 md:mt-2">
                    {/* Treatment Type Badges (BOTOX / REDENSITY 1) */}
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      {promo.hasSkinBooster && (
                        <span className="px-1.5 py-0.5 text-[8px] md:text-[9px] font-semibold rounded bg-teal-50 text-teal-700 border border-teal-200">
                          REDENSITY 1
                        </span>
                      )}
                      {promo.hasBotox && (
                        <span className="px-1.5 py-0.5 text-[8px] md:text-[9px] font-semibold rounded bg-purple-50 text-purple-700 border border-purple-200">
                          BOTOX
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-1.5 md:gap-2 mb-1 md:mb-1.5">
                      <Tag className="w-3 h-3 md:w-4 md:h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-xs md:text-sm text-gray-800 leading-tight line-clamp-1">{promo.name}</p>
                        <p className="text-[9px] md:text-[10px] text-amber-600">{promo.subtitle}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-baseline gap-1.5 md:gap-2 mb-1">
                      <p className="text-lg md:text-xl font-bold text-amber-600">{promo.price}</p>
                    </div>
                    
                    {/* Afterpay - only for non-membership */}
                    {!promo.price.includes('/mo') && (
                      <div className="flex items-center gap-1 mb-1.5 md:mb-2 bg-gradient-to-r from-mint-50 to-teal-50 rounded px-1.5 py-0.5" style={{ background: 'linear-gradient(90deg, #b2fce4 0%, #e0fcf4 100%)' }}>
                        <span className="text-[9px] md:text-[10px] font-bold text-teal-800">afterpay</span>
                        <span className="text-[9px] md:text-[10px] text-teal-700">{getAfterpay(promo.priceNum)}</span>
                      </div>
                    )}
                    
                    <p className="text-[9px] md:text-[10px] text-gray-500 mb-1.5 md:mb-2 line-clamp-1">{promo.reason}</p>
                    
                    {/* Hover CTA */}
                    <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <span className="text-[10px] md:text-xs text-amber-600 font-medium flex items-center gap-1">
                        {isFr ? "Réserver #" : "Book #"}{promo.num} <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Reference + CTA */}
          <div className="px-3 md:px-5 pb-4 md:pb-5">
            <p className="text-center text-[10px] md:text-xs text-gray-500 mb-3 md:mb-4">
              💡 {isFr 
                ? "Cliquez sur un traitement pour voir tous les détails!"
                : "Click any treatment to see full details!"
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
              <Button
                onClick={() => navigate('/skin-boosters')}
                className={`flex-1 bg-gradient-to-r ${sectionGradient} hover:opacity-90 text-white py-2.5 md:py-3 text-sm md:text-base`}
                data-testid="seasonal-book-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {isFr ? "Voir les 30 Traitements" : "View All 30 Treatments"}
              </Button>
              <Button
                variant="outline"
                onClick={onBookNow}
                className="flex-1 py-2.5 md:py-3 border-stone-300 hover:bg-stone-50 text-sm md:text-base"
                data-testid="seasonal-services-btn"
              >
                <Calendar className="w-4 h-4 mr-1" />
                {isFr ? "Réserver maintenant" : "Book Now"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for smooth infinite scroll - 90s for 30 items */}
      <style>{`
        @keyframes smoothScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>

      {/* Inline Booking Flow Modal */}
      <InlineBookingFlow 
        isOpen={showBooking} 
        onClose={() => {
          setShowBooking(false);
          setBookingPackage(null);
        }}
        prefillPackage={bookingPackage}
      />
    </section>
  );
};

export default SeasonalSuggestions;
